
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { OrganizerObject } from '@/store/modules/Auctions/OrganizersModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'organizers-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('organizersUpdating'), [
          translate('auctions'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_ORGANIZER,
        route.params.id
      );
      // delete data.data.logo;

      const organizer = ref<OrganizerObject>(data.data);

      const logo = ref(organizer.value.logo);
      const mapLogo = ref(organizer.value.mapLogo);
      const logoFile = ref<File | null>(null);
      const mapLogoFile = ref<File | null>(null);

      const previewLogo = (e) => {
        const file = e.target.files[0];
        logoFile.value = file;
        logo.value = URL.createObjectURL(file);
      };

      const previewMapLogo = (e) => {
        const file = e.target.files[0];
        mapLogoFile.value = file;
        mapLogo.value = URL.createObjectURL(file);
      };

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const goTo = (pageName: string, id?: number) => {
        if (id) {
          router.push({ name: pageName, params: { id } });
        } else {
          router.push({ name: pageName });
        }
      };

      const validationSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
        phone: Yup.string()
          .required(() => translate('PHONE_NUMBER_IS_REQUIRED_FIELD'))
          .label('Phone'),
        logo: Yup.mixed().label('Logo'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        let logoFormData, videoFormData, mapLogoFormData;
        if (logoFile.value) {
          logoFormData = new FormData();
          logoFormData.append('images', logoFile.value);
        }

        if (mapLogoFile.value) {
          mapLogoFormData = new FormData();
          mapLogoFormData.append('images', mapLogoFile.value);
        }

        if (values.video) {
          videoFormData = new FormData();
          videoFormData.append('video', values.video);
          delete values.video;
        }

        await store.dispatch(Actions.UPDATE_ORGANIZER, {
          id: organizer.value?.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          if (logoFormData) {
            await store.dispatch(Actions.UPLOAD_ORGANIZER_LOGO, {
              data: logoFormData,
              organizerId: organizer.value?.id,
            });
          }

          if (mapLogoFormData) {
            await store.dispatch(Actions.UPLOAD_ORGANIZER_MAP_LOGO, {
              data: mapLogoFormData,
              organizerId: organizer.value?.id,
            });
          }

          if (videoFormData) {
            await store.dispatch(Actions.UPLOAD_ORGANIZER_VIDEO, {
              data: videoFormData,
              organizerId: organizer.value?.id,
            });
          }
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_ORGANIZER'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'organizers-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        organizer,
        can,
        goTo,
        previewLogo,
        previewMapLogo,
        logo,
        mapLogo,
      };
    },
  });
